import { useState } from "react";
import { toast } from "sonner";
import LegalLayout from "./LegalLayout";
import { submitRelease } from "../../lib/api";

export default function Release() {
  const [form, setForm] = useState({
    full_name: "", email: "", session_date: "",
    distribution_permission: false, compensation_notes: "",
    signature: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitRelease(form);
      toast.success(res.message || "Release received.");
      setDone(true);
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <LegalLayout title="Release Agreement & Model Consent" path="/legal/release" description="Model release and consent agreement for recorded fly-on-the-wall erotic hypnosis session videos. All recording is explicitly consented and released only on the terms you sign for.">
      <p>This Release Agreement governs the recording, ownership and distribution of any fly-on-the-wall video produced as part of an in-person session.</p>
      <p>By signing, the participant confirms they are 18 or older, of sound mind, and consent to the recording on the terms specified. Recording will not commence without a signed release.</p>

      <h3 className="font-serif text-2xl text-cream mt-8">Template clauses</h3>
      <ol className="space-y-3 list-decimal pl-5">
        <li><strong className="text-cream">Identity & age:</strong> Participant is 18+ and of legal capacity.</li>
        <li><strong className="text-cream">Session details:</strong> Date, duration and modality recorded.</li>
        <li><strong className="text-cream">Ownership:</strong> Master files are held by the practitioner; participant retains rights specified herein.</li>
        <li><strong className="text-cream">Compensation:</strong> Any compensation (cash, copy of file, etc.) recorded explicitly below.</li>
        <li><strong className="text-cream">Confidentiality:</strong> Both parties hold all materials in strict confidence unless otherwise agreed.</li>
        <li><strong className="text-cream">Distribution permission:</strong> Yes / No - private use, limited distribution, or public sale via approved platforms.</li>
        <li><strong className="text-cream">Withdrawal:</strong> Distribution permission may be withdrawn in writing at any time prior to publication.</li>
        <li><strong className="text-cream">Signatures:</strong> Practitioner and participant, dated.</li>
      </ol>

      <h3 className="font-serif text-2xl text-cream mt-10">Request a Release Form</h3>
      <p>Submit this short form and a tailored Release Agreement PDF will be emailed to you for signature.</p>

      {done ? (
        <div data-testid="release-success" className="border border-gold/40 p-8 bg-gold/5 not-prose mt-6">
          <h4 className="font-serif text-xl text-cream mb-2">Received.</h4>
          <p className="text-sm text-cream/70">A signed PDF will be emailed to you within 48 hours.</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 border border-[#c9a04b]/20 bg-charcoalMuted p-6 mt-6" data-testid="release-form">
          <Field label="Full legal name">
            <input required value={form.full_name} onChange={update("full_name")} data-testid="rl-name" className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold" />
          </Field>
          <Field label="Email">
            <input required type="email" value={form.email} onChange={update("email")} data-testid="rl-email" className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold" />
          </Field>
          <Field label="Session date">
            <input type="date" value={form.session_date} onChange={update("session_date")} data-testid="rl-date" className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold" />
          </Field>
          <Field label="Compensation notes (optional)">
            <textarea rows={2} value={form.compensation_notes} onChange={update("compensation_notes")} data-testid="rl-comp" className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold" />
          </Field>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.distribution_permission} onChange={update("distribution_permission")} data-testid="rl-dist" className="mt-1 accent-[#c9a04b]" />
            <span className="text-sm text-cream/80">I permit distribution beyond private use (subject to final signed PDF).</span>
          </label>
          <Field label="Notes (optional)">
            <textarea rows={2} value={form.notes} onChange={update("notes")} data-testid="rl-notes" className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold" />
          </Field>
          <Field label="Type your full name as signature">
            <input required value={form.signature} onChange={update("signature")} data-testid="rl-sig" className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold font-serif italic" />
          </Field>
          <button type="submit" disabled={loading} className="btn-primary" data-testid="rl-submit">
            {loading ? "Sending…" : "Request Release Form"}
          </button>
        </form>
      )}
    </LegalLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] text-cream/55 block mb-2">{label}</label>
      {children}
    </div>
  );
}
